export interface HistoryEntry {
  id: string;
  query: string;
  timestamp: number;
  resultCount: number;
  filters: string[];
}

interface Props {
  entries: HistoryEntry[];
  onSelect: (query: string) => void;
  onDelete: (id: string) => void;
  onClearAll: () => void;
  visible: boolean;
}

function formatTimestamp(ts: number): string {
  const now = Date.now();
  const diff = now - ts;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  if (hrs < 24) return `${hrs}h ago`;
  if (days === 1) return 'Yesterday';
  if (days < 7) return `${days} days ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function SearchHistoryPanel({ entries, onSelect, onDelete, onClearAll, visible }: Props) {
  if (!visible || entries.length === 0) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border border-slate-200 z-30 overflow-hidden" style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <i className="ri-history-line text-slate-500 text-sm"></i>
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Recent Searches</span>
        </div>
        <button
          onClick={onClearAll}
          className="text-xs text-slate-400 hover:text-red-500 font-medium transition-colors cursor-pointer whitespace-nowrap"
        >
          Clear all
        </button>
      </div>

      {/* Entries */}
      <div className="max-h-64 overflow-y-auto">
        {entries.map((entry) => (
          <div
            key={entry.id}
            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors group border-b border-slate-50 last:border-0"
          >
            {/* Clock icon */}
            <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
              <i className="ri-time-line text-slate-400 text-sm"></i>
            </div>

            {/* Main content — clickable area */}
            <button
              onClick={() => onSelect(entry.query)}
              className="flex-1 text-left min-w-0 cursor-pointer"
            >
              <div className="text-sm font-medium text-slate-900 truncate group-hover:text-violet-700 transition-colors">
                {entry.query}
              </div>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-[11px] text-slate-400 flex items-center gap-1">
                  <i className="ri-file-list-line text-[10px]"></i>
                  {entry.resultCount} result{entry.resultCount !== 1 ? 's' : ''}
                </span>
                <span className="text-[11px] text-slate-300">·</span>
                <span className="text-[11px] text-slate-400">{formatTimestamp(entry.timestamp)}</span>
                {entry.filters.length > 0 && (
                  <>
                    <span className="text-[11px] text-slate-300">·</span>
                    <span className="text-[11px] text-violet-500 font-medium truncate max-w-[120px]">
                      {entry.filters.slice(0, 2).join(', ')}
                      {entry.filters.length > 2 ? ` +${entry.filters.length - 2}` : ''}
                    </span>
                  </>
                )}
              </div>
            </button>

            {/* Re-search arrow — visible on hover */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={() => onSelect(entry.query)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-violet-50 text-slate-400 hover:text-violet-600 transition-colors cursor-pointer"
                title="Search again"
              >
                <i className="ri-search-line text-sm"></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(entry.id); }}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                title="Remove"
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
